import React, { Component } from 'react'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper'
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Spin,
  Tag,
  Empty,
  Popconfirm,
  Icon,
  notification,
} from 'antd'
import moment from 'moment'
import Request, { API_URL } from '../../../request'
import MediaViewer from '../../../components/MediaViewer'
import styles from './styles.less'

const { RangePicker } = DatePicker

const fullUrl = (u) => {
  if (!u) return ''
  if (u.indexOf('http') === 0) return u
  return API_URL + (u.charAt(0) === '/' ? '' : '/') + u
}

const parseMedia = (data) => {
  if (!data) return { photos: [], videos: [] }
  if (Array.isArray(data)) {
    const photos = []
    const videos = []
    data.forEach((m) => {
      const type = (m && m.type) || ''
      if (type === 'video') videos.push(m)
      else photos.push(m)
    })
    return { photos, videos }
  }
  return {
    photos: data.photos || data.images || [],
    videos: data.videos || [],
  }
}

// Route components here are rendered without router props, so read the URL
// directly. The outward review opens this page with ?captureDate= & ?orderIds=.
const urlParams = () => {
  try {
    const p = new URLSearchParams(window.location.search || '')
    return { captureDate: p.get('captureDate') || '', orderIds: p.get('orderIds') || '' }
  } catch (e) {
    return { captureDate: '', orderIds: '' }
  }
}

const cardStyle = {
  background: '#fff',
  borderRadius: 8,
  overflow: 'hidden',
  border: '1px solid #eee',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
}
const mediaStyle = {
  position: 'relative',
  height: 120,
  background: '#000',
  cursor: 'pointer',
}
const mediaInner = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }

// Full page that lists soft-deleted outward media and lets the user restore each
// item back into the outward review.
class DeletedOutwardMedia extends Component {
  constructor(props) {
    super(props)
    const { captureDate, orderIds } = urlParams()
    const day = captureDate ? moment(captureDate, 'YYYY-MM-DD') : null
    this.state = {
      loading: false,
      items: [],
      // when opened from the review, restore exactly the groups it showed
      orderIds: orderIds || '',
      dateRange: day && day.isValid() ? [day.clone().startOf('day'), day.clone().endOf('day')] : null,
      restoringId: null,
      previewIndex: -1,
    }
  }

  componentDidMount() {
    this.fetch()
  }

  fetch = () => {
    const { dateRange, orderIds } = this.state
    const params = { _t: Date.now() }
    // when launched from the review, scope to exactly that group + day; on a
    // manual search orderIds is cleared and only the date range applies
    if (orderIds) params.orderIds = orderIds
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].clone().startOf('day').toISOString()
      params.endDate = dateRange[1].clone().endOf('day').toISOString()
    }
    this.setState({ loading: true })
    Request.deletedOutwardMedia(params).then(({ error, data }) => {
      if (!error) {
        const { photos, videos } = parseMedia(data)
        this.setState({ loading: false, items: [...photos, ...videos] })
      } else {
        this.setState({ loading: false, items: [] })
        notification.error({ message: 'Failed to load deleted images' })
      }
    })
  }

  // any manual filter change abandons the from-review orderIds and uses the date
  handleSearch = () => this.setState({ orderIds: '' }, this.fetch)

  handleReset = () => this.setState({ orderIds: '', dateRange: null }, this.fetch)

  restore = async (item) => {
    if (!item || !item._id) return
    this.setState({ restoringId: item._id })
    const res = await Request.restoreOutwardMedia({ mediaId: item._id })
    if (res && !res.error) {
      notification.success({ message: 'Media restored' })
      this.setState((s) => ({
        items: s.items.filter((m) => String(m._id) !== String(item._id)),
        restoringId: null,
      }))
    } else {
      notification.error({ message: (res && res.message) || 'Failed to restore media' })
      this.setState({ restoringId: null })
    }
  }

  getMediaItems = () => this.state.items.filter((m) => m && m.url)

  openPreview = (item) => {
    const idx = this.getMediaItems().findIndex(
      (m) => String(m._id) === String(item._id)
    )
    this.setState({ previewIndex: idx >= 0 ? idx : 0 })
  }

  closePreview = () => this.setState({ previewIndex: -1 })

  renderCard = (item) => {
    const url = fullUrl(item.url)
    const isVideo = item.type === 'video'
    const restoring = this.state.restoringId === item._id
    return (
      <Col xs={12} sm={8} md={6} lg={4} key={item._id} style={{ marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={mediaStyle} title="Click to preview" onClick={() => this.openPreview(item)}>
            {isVideo ? (
              <video src={url} style={mediaInner} muted preload="metadata" />
            ) : (
              <img src={url} alt="" style={mediaInner} loading="lazy" />
            )}
            <span
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon type="eye" />
            </span>
          </div>
          <div style={{ padding: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {item.orderId ? <Tag style={{ margin: 0 }}>Order #{item.orderId}</Tag> : null}
              {item.userName ? <Tag style={{ margin: 0 }}>{item.userName}</Tag> : null}
              {item.subCategory ? <Tag style={{ margin: 0 }}>{item.subCategory}</Tag> : null}
              {item.deletedAt ? (
                <Tag style={{ margin: 0 }} color="red">
                  Deleted {moment(item.deletedAt).format('DD-MM-YYYY')}
                </Tag>
              ) : null}
            </div>
            <Popconfirm
              title="Restore this media?"
              okText="Restore"
              cancelText="Cancel"
              onConfirm={() => this.restore(item)}
            >
              <Button size="small" type="primary" icon="rollback" loading={restoring} block>
                Restore
              </Button>
            </Popconfirm>
          </div>
        </div>
      </Col>
    )
  }

  render() {
    const { loading, dateRange } = this.state
    const items = this.getMediaItems()
    return (
      <PageHeaderWrapper title="Deleted Outward Images">
        <Card className={styles.filterBar} bodyStyle={{ paddingBottom: 0 }}>
          <Row gutter={12}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <RangePicker
                style={{ width: '100%', marginBottom: 12 }}
                value={dateRange}
                onChange={(val) => this.setState({ dateRange: val })}
              />
            </Col>
            <Col xs={24} sm={12} md={16} lg={16}>
              <Button
                type="primary"
                icon="search"
                onClick={this.handleSearch}
                style={{ marginRight: 8, marginBottom: 12 }}
              >
                Search
              </Button>
              <Button icon="reload" onClick={this.handleReset} style={{ marginRight: 8, marginBottom: 12 }}>
                Reset
              </Button>
              <Button icon="sync" loading={loading} onClick={this.fetch} style={{ marginBottom: 12 }}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        <Card bordered>
          <Spin spinning={loading}>
            <div style={{ marginBottom: 12, color: '#888' }}>
              {items.length
                ? `${items.length} deleted ${items.length === 1 ? 'image' : 'images'}`
                : null}
            </div>
            {items.length ? (
              <Row gutter={12}>{items.map((item) => this.renderCard(item))}</Row>
            ) : (
              !loading && (
                <Empty description="No deleted images" style={{ padding: '40px 0' }} />
              )
            )}
          </Spin>
        </Card>

        <MediaViewer
          lightboxOnly
          items={items}
          openIndex={this.state.previewIndex}
          onClose={this.closePreview}
        />
      </PageHeaderWrapper>
    )
  }
}

export default DeletedOutwardMedia
